import React, { useState, useMemo } from 'react'
import { useCurrency } from '../context/CurrencyContext'

// ── MATH ─────────────────────────────────────────────────────────────────────

function simulatePayoff(loans, extraMonthly, strategy) {
  const active = loans
    .filter(l => !l.is_settled && parseFloat(l.balance) > 1)
    .map(l => ({
      id: l.id, name: l.name,
      balance: parseFloat(l.balance),
      rate: parseFloat(l.interest_rate) || 0,
      minEmi: parseFloat(l.emi_amount) + parseFloat(l.extra_emi_amount || 0),
    }))

  if (!active.length) return { months: 0, totalInterest: 0, schedule: [], payoffOrder: [] }

  const state = [...active].sort((a, b) =>
    strategy === 'avalanche' ? b.rate - a.rate : a.balance - b.balance
  ).map(l => ({ ...l }))

  let rolledEmi = 0
  const schedule = []
  let totalInterest = 0
  let month = 0
  const payoffOrder = []
  const MAX_MONTHS = 600

  while (state.length > 0 && month < MAX_MONTHS) {
    month++
    const monthRow = { month, payments: [] }
    const toPop = []

    for (let i = 0; i < state.length; i++) {
      const loan = state[i]
      const interest = loan.rate > 0 ? loan.balance * (loan.rate / 12 / 100) : 0
      let payment = loan.minEmi + (i === 0 ? extraMonthly + rolledEmi : 0)
      payment = Math.min(payment, loan.balance + interest)
      const principalPaid = Math.max(0, payment - interest)
      totalInterest += interest
      loan.balance = Math.max(0, loan.balance - principalPaid)

      monthRow.payments.push({
        id: loan.id, name: loan.name,
        payment: Math.round(payment),
        interest: Math.round(interest),
        principal: Math.round(principalPaid),
        balance: Math.round(loan.balance),
        rate: loan.rate,
      })

      if (loan.balance <= 1) toPop.push(i)
    }

    schedule.push(monthRow)

    for (let i = toPop.length - 1; i >= 0; i--) {
      const idx = toPop[i]
      payoffOrder.push({ name: state[idx].name, month, rate: state[idx].rate })
      rolledEmi += state[idx].minEmi
      state.splice(idx, 1)
    }
  }

  return { months: month, totalInterest: Math.round(totalInterest), schedule, payoffOrder }
}

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function addMonthsLabel(n) {
  if (!n) return 'N/A'
  const d = new Date()
  d.setMonth(d.getMonth() + n)
  return MONTHS_SHORT[d.getMonth()] + ' ' + d.getFullYear()
}

function monthLabel(n) {
  const d = new Date()
  d.setMonth(d.getMonth() + n - 1)
  return MONTHS_SHORT[d.getMonth()] + " '" + String(d.getFullYear()).slice(2)
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export default function SmartPayoffEngine({ loans, isPaid, onFeatureLock }) {
  const { fmt } = useCurrency()
  const [strategy, setStrategy] = useState('avalanche')
  const [extraPayment, setExtraPayment] = useState(0)
  const [extraInput, setExtraInput] = useState('')
  const [showFullSchedule, setShowFullSchedule] = useState(false)
  const [activeTab, setActiveTab] = useState('plan')

  const activeLoans = useMemo(() =>
    loans.filter(l => !l.is_settled && parseFloat(l.balance) > 1),
    [loans]
  )

  const totalMonthlyEmi = activeLoans.reduce((s, l) =>
    s + parseFloat(l.emi_amount) + parseFloat(l.extra_emi_amount || 0), 0)

  const basePlan      = useMemo(() => simulatePayoff(activeLoans, 0, strategy),            [activeLoans, strategy])
  const extraPlan     = useMemo(() => simulatePayoff(activeLoans, extraPayment, strategy),  [activeLoans, extraPayment, strategy])
  const avalanchePlan = useMemo(() => simulatePayoff(activeLoans, extraPayment, 'avalanche'),[activeLoans, extraPayment])
  const snowballPlan  = useMemo(() => simulatePayoff(activeLoans, extraPayment, 'snowball'), [activeLoans, extraPayment])

  const monthsSaved   = basePlan.months - extraPlan.months
  const interestSaved = basePlan.totalInterest - extraPlan.totalInterest

  const displaySchedule = showFullSchedule
    ? extraPlan.schedule
    : extraPlan.schedule.slice(0, 24)

  const panelStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }
  const hdrStyle   = { padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!activeLoans.length) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📊</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.4rem', color: 'var(--text)' }}>No active loans</div>
        <div style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>Add loans to generate your Smart Payoff Plan.</div>
      </div>
    )
  }

  // ── Free user teaser ─────────────────────────────────────────────────────────
  if (!isPaid) {
    return (
      <div>
        <div style={{ marginBottom: '1.4rem' }}>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0 }}>Smart Payoff Engine</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Your personalised month-by-month debt elimination plan.</p>
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{ filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.55 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {['Debt-Free Date', 'Total Interest', 'Months Left', 'Monthly EMI'].map((label, i) => (
                <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{label}</div>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: 'var(--text)' }}>
                    {['Mar 2031', '₹4.8L', '58 mo', '₹62,000'][i]}
                  </div>
                </div>
              ))}
            </div>
            <div style={panelStyle}>
              <div style={hdrStyle}><span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Recommended Payoff Order</span></div>
              <div style={{ padding: '0.5rem 0' }}>
                {activeLoans.slice(0, 3).map((l, i) => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.85rem 1.4rem', borderBottom: i < 2 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? '#c9a84c' : 'var(--surface-hdr)', color: i === 0 ? '#0d0d0d' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', flexShrink: 0 }}>{i + 1}</div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.88rem' }}>{l.name}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>{l.interest_rate}% p.a.</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'var(--surface)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '12px', padding: '2rem 2.5rem', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', maxWidth: '340px' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🔒</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '700', color: 'var(--text)', marginBottom: '0.5rem' }}>Smart Payoff Engine</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                Get your personalised month-by-month debt elimination plan, payoff order, interest savings simulator and exact debt-free date.
              </div>
              <button
                onClick={() => onFeatureLock?.('Smart Payoff Engine', 'Get your personalised month-by-month debt elimination plan showing exactly which loan to pay first, how much extra to put where, and your exact debt-free date — with DebtCrush Premium (₹149/month).')}
                style={{ background: '#c9a84c', color: '#0d0d0d', border: 'none', padding: '0.6rem 1.4rem', borderRadius: '5px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.03em' }}
              >
                Upgrade — ₹149/month
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Paid — Full Engine ───────────────────────────────────────────────────────

  const tabStyle = (active) => ({
    padding: '0.5rem 1rem', fontFamily: 'monospace', fontSize: '0.7rem',
    fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.06em',
    cursor: 'pointer', border: '1px solid',
    borderColor: active ? 'var(--text)' : 'var(--border)',
    background: active ? 'var(--text)' : 'transparent',
    color: active ? 'var(--bg)' : 'var(--text-muted)',
    borderRadius: '4px', transition: 'all 0.15s',
  })

  const stratBtnStyle = (active) => ({
    flex: 1, padding: '0.48rem 0.75rem', fontFamily: 'monospace', fontSize: '0.68rem',
    fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em',
    cursor: 'pointer', border: '1.5px solid',
    borderColor: active ? '#c9a84c' : 'var(--border)',
    background: active ? 'rgba(201,168,76,0.1)' : 'transparent',
    color: active ? '#c9a84c' : 'var(--text-muted)',
    borderRadius: '4px', transition: 'all 0.15s',
  })

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1.4rem' }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0 }}>Smart Payoff Engine</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Your personalised month-by-month debt elimination plan. ✦ Premium</p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button style={stratBtnStyle(strategy === 'avalanche')} onClick={() => setStrategy('avalanche')}>🔥 Avalanche</button>
          <button style={stratBtnStyle(strategy === 'snowball')}  onClick={() => setStrategy('snowball')}>⚡ Snowball</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Debt-Free Date',   value: addMonthsLabel(extraPlan.months),  color: '#4a6741' },
          { label: 'Total Interest',   value: fmt(extraPlan.totalInterest),       color: '#b8441a' },
          { label: 'Months Remaining', value: extraPlan.months + ' mo',           color: 'var(--text)' },
          { label: 'Total Monthly EMI',value: fmt(totalMonthlyEmi),               color: '#3d4a5c' },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{c.label}</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.25rem', fontWeight: '700', color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Extra Payment Simulator */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Extra Payment Simulator</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Extra per month ₹</span>
            <input
              type="text" inputMode="decimal" placeholder="e.g. 5000"
              value={extraInput}
              onChange={e => { setExtraInput(e.target.value); setExtraPayment(parseFloat(e.target.value) || 0) }}
              style={{ background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.45rem 0.75rem', fontSize: '0.88rem', fontFamily: 'DM Sans,sans-serif', color: 'var(--text)', width: '120px', outline: 'none' }}
            />
          </div>
          {extraPayment > 0 && monthsSaved > 0 && (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '6px', padding: '0.45rem 0.9rem' }}>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700', color: '#4a6741' }}>{monthsSaved}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741', marginLeft: '0.3rem' }}>months saved</span>
              </div>
              <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '6px', padding: '0.45rem 0.9rem' }}>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700', color: '#4a6741' }}>{fmt(interestSaved)}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741', marginLeft: '0.3rem' }}>interest saved</span>
              </div>
              <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '6px', padding: '0.45rem 0.9rem' }}>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: '#c9a84c' }}>🏁 {addMonthsLabel(extraPlan.months)}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: '#c9a84c', marginLeft: '0.3rem' }}>new debt-free date</span>
              </div>
            </div>
          )}
          {extraPayment > 0 && monthsSaved === 0 && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)' }}>Enter a higher amount to see impact</span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[['plan', '📋 Payoff Plan'], ['schedule', '📅 Schedule'], ['compare', '⚖️ Compare']].map(([id, label]) => (
          <button key={id} style={tabStyle(activeTab === id)} onClick={() => setActiveTab(id)}>{label}</button>
        ))}
      </div>

      {/* TAB: Payoff Plan */}
      {activeTab === 'plan' && (
        <div style={panelStyle}>
          <div style={hdrStyle}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>
              {strategy === 'avalanche' ? '🔥 Avalanche' : '⚡ Snowball'} — Recommended Payoff Order
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
              {strategy === 'avalanche'
                ? 'Attack highest interest first — saves the most money overall'
                : 'Attack smallest balance first — fastest psychological wins'}
            </div>
          </div>
          <div style={{ padding: '0.5rem 0' }}>
            {extraPlan.payoffOrder.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.4rem', borderBottom: i < extraPlan.payoffOrder.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: i === 0 ? '#c9a84c' : 'var(--surface-hdr)', color: i === 0 ? '#0d0d0d' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.88rem' }}>{item.name}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                    {item.rate}% p.a. · Paid off in month {item.month} ({addMonthsLabel(item.month)})
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: '#4a6741' }}>Month {item.month}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>{addMonthsLabel(item.month)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: Schedule */}
      {activeTab === 'schedule' && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...hdrStyle }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Month-by-Month Schedule</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>{extraPlan.months} months total</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.72rem' }}>
              <thead>
                <tr style={{ background: 'var(--surface-hdr)' }}>
                  {['Month', 'Loan', 'Payment', 'Principal', 'Interest', 'Balance'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 1rem', textAlign: h === 'Month' || h === 'Loan' ? 'left' : 'right', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.62rem', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displaySchedule.map((row, ri) =>
                  row.payments.map((p, pi) => (
                    <tr key={`${ri}-${pi}`} style={{ borderBottom: '1px solid var(--surface-hdr)', background: ri % 2 === 0 ? 'transparent' : 'rgba(201,168,76,0.02)' }}>
                      {pi === 0 && (
                        <td rowSpan={row.payments.length} style={{ padding: '0.5rem 1rem', color: 'var(--text-muted)', verticalAlign: 'top', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                          {monthLabel(row.month)}
                        </td>
                      )}
                      <td style={{ padding: '0.5rem 1rem', color: 'var(--text)', fontWeight: '500' }}>{p.name}</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{fmt(p.payment)}</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#4a6741' }}>{fmt(p.principal)}</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#b8441a' }}>{fmt(p.interest)}</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: p.balance <= 1 ? '#4a6741' : 'var(--text)', fontWeight: p.balance <= 1 ? '700' : '400' }}>
                        {p.balance <= 1 ? '✓ Paid off' : fmt(p.balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {extraPlan.schedule.length > 24 && (
            <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)', background: 'var(--surface-hdr)', textAlign: 'center' }}>
              <button
                onClick={() => setShowFullSchedule(v => !v)}
                style={{ background: 'none', border: '1px solid var(--border)', padding: '0.35rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.65rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                {showFullSchedule ? '▲ Show less' : `▼ Show all ${extraPlan.months} months`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB: Compare */}
      {activeTab === 'compare' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
            {[
              { label: '🔥 Avalanche Method', plan: avalanchePlan, color: '#b8441a', desc: 'Highest interest first — saves the most money' },
              { label: '⚡ Snowball Method',  plan: snowballPlan,  color: '#3d4a5c', desc: 'Smallest balance first — fastest psychological wins' },
            ].map(({ label, plan, color, desc }) => (
              <div key={label} style={panelStyle}>
                <div style={hdrStyle}>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700' }}>{label}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{desc}</div>
                </div>
                <div style={{ padding: '1.1rem 1.4rem' }}>
                  {[
                    { label: 'Debt-Free Date', value: addMonthsLabel(plan.months), color: '#4a6741' },
                    { label: 'Total Months',   value: plan.months + ' months',     color: 'var(--text)' },
                    { label: 'Total Interest', value: fmt(plan.totalInterest),     color },
                  ].map(r => (
                    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.55rem 0', borderBottom: '1px solid var(--surface-hdr)' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{r.label}</span>
                      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: r.color }}>{r.value}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: '0.85rem' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Payoff Order</div>
                    {plan.payoffOrder.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color, fontWeight: '700', minWidth: '14px' }}>{i + 1}.</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '500', flex: 1 }}>{item.name}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>Mo {item.month}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Winner banner */}
          {avalanchePlan.totalInterest !== snowballPlan.totalInterest && (
            <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '8px', padding: '1rem 1.4rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>🏆</span>
              <div>
                <span style={{ fontWeight: '600', fontSize: '0.88rem' }}>
                  {avalanchePlan.totalInterest <= snowballPlan.totalInterest ? 'Avalanche' : 'Snowball'} saves you more money
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                  — {fmt(Math.abs(avalanchePlan.totalInterest - snowballPlan.totalInterest))} less in total interest
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
